// 📄 pages/Clientes/DetalleCliente.tsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Ban, RotateCcw, RefreshCw, Save } from 'lucide-react';
import { getCliente, updateCliente, setClienteActivo, Cliente, APP_OPTIONS } from '../../services/clienteService.ts';
import CheckboxCardGroup from '../../components/ui/CheckboxCardGroup.tsx';
import ConfirmDialog from '../../components/ui/ConfirmDialog.tsx';
import { PAISES } from '../../data/paises.ts';

export default function DetalleCliente() {
  const { clienteId } = useParams<{ clienteId: string }>();
  const id = clienteId ? decodeURIComponent(clienteId) : '';

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [email, setEmail] = useState('');
  const [apps, setApps] = useState<string[]>([]);
  const [sector, setSector] = useState('');
  const [pais, setPais] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmInactivar, setShowConfirmInactivar] = useState(false);

  const cargar = () => {
    setLoading(true);
    getCliente(id)
      .then((c) => {
        setCliente(c);
        if (c) {
          setEmail(c.email);
          setApps(c.apps);
          setSector(c.sector || '');
          setPais(c.pais || '');
          setDescripcion(c.descripcion || '');
        }
      })
      .catch((err) => console.error('Error cargando cliente:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (id) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleGuardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await updateCliente(id, { email, apps, sector, pais, descripcion });
      setModoEdicion(false);
      cargar();
    } catch (err: any) {
      setError(err.message || 'Error al guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelarEdicion = () => {
    if (cliente) {
      setEmail(cliente.email);
      setApps(cliente.apps);
      setSector(cliente.sector || '');
      setPais(cliente.pais || '');
      setDescripcion(cliente.descripcion || '');
    }
    setError(null);
    setModoEdicion(false);
  };

  const handleInactivar = async () => {
    setSaving(true);
    try {
      await setClienteActivo(id, false);
      setShowConfirmInactivar(false);
      cargar();
    } catch (err) {
      console.error('Error al inactivar el cliente:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReactivar = async () => {
    setSaving(true);
    try {
      await setClienteActivo(id, true);
      cargar();
    } catch (err) {
      console.error('Error al reactivar el cliente:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando cliente...</p>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-white">
        <p className="text-sm text-gray-400">No se encontró el cliente.</p>
        <Link to="/clientes" className="text-blue-400 hover:underline text-sm">Volver a Clientes</Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto text-white">
      <Link
        to="/clientes"
        className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a Clientes
      </Link>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-semibold">{cliente.name}</h1>
              {!cliente.activo && (
                <span className="text-[10px] bg-gray-700 text-gray-300 border border-white/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Inactivo
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500">El nombre del cliente no se puede modificar por ahora.</p>
          </div>

          {!modoEdicion && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setModoEdicion(true)}
                className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg transition-all"
              >
                <Pencil className="w-3.5 h-3.5" /> Editar
              </button>
              {cliente.activo ? (
                <button
                  onClick={() => setShowConfirmInactivar(true)}
                  className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-red-500/10 border border-white/10 hover:border-red-500/30 text-gray-300 hover:text-red-400 px-3 py-1.5 rounded-lg transition-all"
                >
                  <Ban className="w-3.5 h-3.5" /> Inactivar
                </button>
              ) : (
                <button
                  onClick={handleReactivar}
                  disabled={saving}
                  className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-emerald-500/10 border border-white/10 hover:border-emerald-500/30 text-gray-300 hover:text-emerald-400 px-3 py-1.5 rounded-lg transition-all disabled:opacity-50"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> {saving ? 'Reactivando...' : 'Reactivar'}
                </button>
              )}
            </div>
          )}
        </div>

        {modoEdicion ? (
          <form onSubmit={handleGuardar} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                Email de contacto
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                Aplicaciones
              </label>
              <CheckboxCardGroup options={APP_OPTIONS} selected={apps} onChange={setApps} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  Sector económico
                </label>
                <input
                  type="text"
                  value={sector}
                  onChange={(e) => setSector(e.target.value)}
                  placeholder="Ej: Retail, Manufactura, Alimentos..."
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  País
                </label>
                <select
                  value={pais}
                  onChange={(e) => setPais(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Selecciona un país</option>
                  {PAISES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                Descripción
              </label>
              <textarea
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                rows={3}
                placeholder="A qué se dedica el cliente, contexto relevante del negocio..."
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleCancelarEdicion}
                className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                <Save className="w-4 h-4" /> {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Email de contacto</h4>
              <p className="text-sm text-gray-300">{cliente.email || '—'}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Aplicaciones</h4>
              {cliente.apps.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {cliente.apps.map(app => (
                    <span key={app} className="text-xs bg-white/5 border border-white/10 px-2.5 py-1 rounded-full text-gray-300">
                      {app}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Ninguna seleccionada.</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Sector económico</h4>
                <p className="text-sm text-gray-300">{cliente.sector || '—'}</p>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">País</h4>
                <p className="text-sm text-gray-300">{cliente.pais || '—'}</p>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Descripción</h4>
              <p className="text-sm text-gray-300 whitespace-pre-wrap">{cliente.descripcion || '—'}</p>
            </div>
          </div>
        )}
      </div>

      {showConfirmInactivar && (
        <ConfirmDialog
          title="¿Inactivar este cliente?"
          message="Vas a poder reactivarlo cuando quieras desde la ficha del cliente. No se borra ningún dato, credencial ni proveedor asociado."
          confirmLabel={saving ? 'Inactivando...' : 'Inactivar'}
          danger
          onConfirm={handleInactivar}
          onCancel={() => setShowConfirmInactivar(false)}
        />
      )}
    </div>
  );
}
